import { styled } from "@mui/material/styles";
import BaseModal from "@reusables/Modals/BaseModal";
import { CornerDownLeft, Delete, MoveDown, MoveLeft, MoveRight, MoveUp, X } from "lucide-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCommandDropdown } from "../context";
import { ModalProps } from "../types";

const KeybindsSection = styled("div")(
    () => `
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    padding-top: 12px;
    border-top: 1px solid #E5E5E5;
    color: #9B93A2;
    font-size: 12px;
    user-select: none;
`
);

const KeybindItem = styled("div")(
    () => `
    display: flex;
    align-items: center;
    gap: 6px;
`
);

const KeybindKey = styled("span")(
    () => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    padding: 2px 6px;
    border: 1px solid #E5E5E5;
    border-radius: 6px;
        background-color: #F5F5F5;
    color: #3C3769;
    font-weight: 500;
    height: 24px;
`
);

const SelectedItemsSection = styled("div")(
    () => `
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #E5E5E5;
`
);

const SelectedItemChip = styled("button")(
    () => `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 8px;
    border-radius: 6px;
    background-color: #F3F2EF;
    color: #3C3769;
    border: 1px solid transparent;
    cursor: pointer;
    font-size: 13px;
    line-height: 1;

    &:hover {
        background-color: #EDEBE6;
    }
`
);

export function Modal({ children, title }: ModalProps) {
    const { t } = useTranslation("", { keyPrefix: "general.components.command" });
    const {
        isOpen,
        setIsOpen,
        navigationStack,
        popNavigation,
        enableKeyboard,
        activeIndex,
        setActiveIndex,
        itemCount,
        triggerActiveItem,
        searchValue,
        initializeNavigationForSelection,
        selections,
        removeSelection,
        multiple,
    } = useCommandDropdown();

    // Modal
    // - Owns global keyboard handling and lifecycle for the open dropdown.
    // - Mirrors keybind help text and hosts selected chips (multiple mode).

    // Track if we've initialized navigation for this modal open
    const hasInitializedRef = useRef(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    const hasNavigation = navigationStack.length > 0;

    // Keyboard navigation handler:
    // - ArrowUp/Down changes active index
    // - Enter selects active item
    // - ArrowRight drills into children (if available)
    // - ArrowLeft navigates back (when not searching)
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!enableKeyboard || !isOpen) return;

            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    setActiveIndex(Math.min(activeIndex + 1, itemCount - 1));
                    break;

                case "ArrowUp":
                    event.preventDefault();
                    setActiveIndex(Math.max(activeIndex - 1, 0));
                    break;

                case "Enter":
                    event.preventDefault();
                    triggerActiveItem("select");
                    break;

                case "Escape":
                    event.preventDefault();
                    if (hasNavigation) {
                        popNavigation();
                    } else {
                        setIsOpen(false);
                    }
                    break;

                case "ArrowLeft":
                    // Go back if not typing in search
                    if (hasNavigation && !searchValue) {
                        event.preventDefault();
                        popNavigation();
                    }
                    break;

                case "ArrowRight":
                    // Drill down
                    event.preventDefault();
                    triggerActiveItem("drill");
                    break;
            }
        },
        [
            enableKeyboard,
            isOpen,
            activeIndex,
            setActiveIndex,
            itemCount,
            triggerActiveItem,
            hasNavigation,
            popNavigation,
            setIsOpen,
            searchValue,
        ]
    );

    // Attach keyboard listener when modal is open
    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, [isOpen, handleKeyDown]);

    // Initialize navigation to selected item when modal opens (single mode only)
    useEffect(() => {
        if (isOpen && !hasInitializedRef.current) {
            const didInit = initializeNavigationForSelection();
            if (didInit) {
                hasInitializedRef.current = true;
            }
        }
        if (!isOpen) {
            // Reset the flag when modal closes
            hasInitializedRef.current = false;
        }
    }, [isOpen, selections, initializeNavigationForSelection]);

    const processedChildren = React.useMemo(() => {
        return React.Children.map(children, (child) => child);
    }, [children]);

    const getSelectionLabel = (selection: { path: Array<string | number>; labels: string[] }) => {
        if (selection.labels.length > 0) {
            return selection.labels.join(" > ");
        }
        return selection.path.map(String).join(" > ");
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            position={"top"}
            padding={"56px"}
            width={700}
            useCloseIcon
        >
            <div className="space-y-[32px]">
                {title && (
                    <div className="text-lg font-semibold text-center text-accent">{title}</div>
                )}
                {processedChildren}
                {selections.length > 0 && (
                    <SelectedItemsSection>
                        {selections.map((selection) => (
                            <SelectedItemChip
                                key={selection.path.join("-")}
                                onClick={() => removeSelection(selection.path)}
                                type="button"
                                aria-label={`Remove ${getSelectionLabel(selection)}`}
                            >
                                <span>{getSelectionLabel(selection)}</span>
                                <X size={12} />
                            </SelectedItemChip>
                        ))}
                    </SelectedItemsSection>
                )}
                <KeybindsSection>
                    <KeybindItem>
                        <KeybindKey>
                            <MoveUp size={14} />
                        </KeybindKey>
                        <KeybindKey>
                            <MoveDown size={14} />
                        </KeybindKey>
                        <span>{t("keys.navigate")}</span>
                    </KeybindItem>
                    <KeybindItem>
                        <KeybindKey>
                            <MoveRight size={14} />
                        </KeybindKey>
                        <span>{t("keys.drill")}</span>
                    </KeybindItem>
                    <KeybindItem>
                        <KeybindKey>
                            <CornerDownLeft size={14} />
                        </KeybindKey>
                        <span>{t("keys.select")}</span>
                    </KeybindItem>
                    <KeybindItem>
                        <KeybindKey>{t("keys.esc")}</KeybindKey>
                        <span>{t("keys.close")}</span>
                    </KeybindItem>
                    {hasNavigation && (
                        <KeybindItem>
                            <KeybindKey>
                                <MoveLeft size={14} />
                            </KeybindKey>
                            <KeybindKey>
                                <Delete size={14} />
                            </KeybindKey>
                            <span>{t("keys.back")}</span>
                        </KeybindItem>
                    )}
                </KeybindsSection>
            </div>
        </BaseModal>
    );
}

export default Modal;
